import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { FaBug, FaCheckCircle, FaHourglassHalf, FaSpinner, FaSearch, FaPaperclip, FaTimes, FaPlus, FaChevronDown, FaChevronUp, FaUser, FaCalendar, FaEdit, FaTrash, FaFilter, FaCalendarAlt, FaCopy } from 'react-icons/fa';
import './Bugs.css';
import { AuthContext } from '../context/AuthContext';
import AttachmentPreview from '../components/AttachmentPreview';

const API_BASE = process.env.REACT_APP_API_URL || 'https://intranet.nesimltd.co.ke';

// Helper function to construct absolute URLs for attachments
const getAbsoluteUrl = (relativeUrl) => {
  if (!relativeUrl) return null;
  
  // If it's already an absolute URL, return as is
  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl;
  }
  
  // If it starts with /, it's a relative path from the backend root
  if (relativeUrl.startsWith('/')) {
    return `${API_BASE}${relativeUrl}`;
  }
  
  // Otherwise, assume it's relative to the uploads directory
  return `${API_BASE}/uploads/support/${relativeUrl}`;
};

// Helper function to check if a file exists and return a fallback if not
const getSafeImageUrl = (relativeUrl) => {
  if (!relativeUrl) return null;
  
  const absoluteUrl = getAbsoluteUrl(relativeUrl);
  console.log('🔍 Checking image URL:', absoluteUrl);
  
  // For now, return the URL and let the browser handle 404s gracefully
  // In a production app, you might want to implement a proper fallback image
  return absoluteUrl;
};

const STATUS_COLORS = {
  'New': '#e57373',
  'Assigned': '#ffb74d',
  'In Progress (Accepted)': '#4ecdc4',
  'Fixed': '#81c784',
  'Fixed (Verified)': '#4caf50',
  'Won\'t fix (Infeasible)': '#9e9e9e',
  'Won\'t fix (Not reproducible)': '#9e9e9e',
  'Won\'t fix (Obsolete)': '#9e9e9e',
  'Won\'t fix (Intended behavior)': '#9e9e9e',
  'Duplicate': '#ff9800',
};

const STATUS_OPTIONS = [
  'New',
  'Assigned',
  'In Progress (Accepted)',
  'Fixed',
  'Fixed (Verified)',
  'Won\'t fix (Infeasible)',
  'Won\'t fix (Not reproducible)',
  'Won\'t fix (Obsolete)',
  'Won\'t fix (Intended behavior)',
  'Duplicate'
];

const PRIORITY_OPTIONS = ['Critical', 'High', 'Medium', 'Low'];
const MODULE_ICONS = {
  'Dashboard': <span title="Dashboard" style={{fontSize:'1.1em'}}>📊</span>,
  'Organization Structure': <span title="Organization Structure" style={{fontSize:'1.1em'}}>🏢</span>,
  'Workflows': <span title="Workflows" style={{fontSize:'1.1em'}}>🔄</span>,
  'Company OKRs': <span title="Company OKRs" style={{fontSize:'1.1em'}}>🎯</span>,
  'Personal OKRs': <span title="Personal OKRs" style={{fontSize:'1.1em'}}>👤</span>,
  'Team OKRs': <span title="Team OKRs" style={{fontSize:'1.1em'}}>👥</span>,
  'My OKRs': <span title="My OKRs" style={{fontSize:'1.1em'}}>📋</span>,
  'Annual OKRs': <span title="Annual OKRs" style={{fontSize:'1.1em'}}>📅</span>,
  'People': <span title="People" style={{fontSize:'1.1em'}}>👥</span>,
  'Teams': <span title="Teams" style={{fontSize:'1.1em'}}>👥</span>,
  'Departments': <span title="Departments" style={{fontSize:'1.1em'}}>🏢</span>,
  'Visual Map': <span title="Visual Map" style={{fontSize:'1.1em'}}>🗺️</span>,
  'Safety Procedures': <span title="Safety Procedures" style={{fontSize:'1.1em'}}>🛡️</span>,
  'SLAs': <span title="SLAs" style={{fontSize:'1.1em'}}>⏱️</span>,
  'Chat': <span title="Chat" style={{fontSize:'1.1em'}}>💬</span>,
  'Cost Cards': <span title="Cost Cards" style={{fontSize:'1.1em'}}>💸</span>,
  'Template Picker': <span title="Template Picker" style={{fontSize:'1.1em'}}>📄</span>,
  'Create Cost Card': <span title="Create Cost Card" style={{fontSize:'1.1em'}}>➕</span>,
  'Edit Cost Card': <span title="Edit Cost Card" style={{fontSize:'1.1em'}}>✏️</span>,
  'Create Custom Template': <span title="Create Custom Template" style={{fontSize:'1.1em'}}>📝</span>,
  'Edit Custom Template': <span title="Edit Custom Template" style={{fontSize:'1.1em'}}>✏️</span>,
  'Create OKR': <span title="Create OKR" style={{fontSize:'1.1em'}}>➕</span>,
  'Edit OKR': <span title="Edit OKR" style={{fontSize:'1.1em'}}>✏️</span>,
  'Technical Sales Workflow': <span title="Technical Sales Workflow" style={{fontSize:'1.1em'}}>🔧</span>,
  'Store Management Workflow': <span title="Store Management Workflow" style={{fontSize:'1.1em'}}>🏪</span>,
  'Sales Workflow': <span title="Sales Workflow" style={{fontSize:'1.1em'}}>💰</span>,
  'Import Workflow': <span title="Import Workflow" style={{fontSize:'1.1em'}}>📦</span>,
  'Job Descriptions': <span title="Job Descriptions" style={{fontSize:'1.1em'}}>📋</span>,
  'Employees Reviews': <span title="Employees Reviews" style={{fontSize:'1.1em'}}>📝</span>,
  'Interview Processes': <span title="Interview Processes" style={{fontSize:'1.1em'}}>🤝</span>,
  'Company Strategy': <span title="Company Strategy" style={{fontSize:'1.1em'}}>🎯</span>,
  'Business Continuity Plan': <span title="Business Continuity Plan" style={{fontSize:'1.1em'}}>📋</span>,
  'Data Management SOP': <span title="Data Management SOP" style={{fontSize:'1.1em'}}>📊</span>,
  'Procurement SOP': <span title="Procurement SOP" style={{fontSize:'1.1em'}}>🛒</span>,
  'Risk Compliance SOP': <span title="Risk Compliance SOP" style={{fontSize:'1.1em'}}>⚠️</span>,
  'Settings': <span title="Settings" style={{fontSize:'1.1em'}}>⚙️</span>,
  'Search Results': <span title="Search Results" style={{fontSize:'1.1em'}}>🔍</span>,
  'Other': <span title="Other" style={{fontSize:'1.1em'}}>❓</span>,
  default: <FaBug style={{fontSize:'1.1em'}} />
};

function StatusBadge({ status, bugId, isDuplicate, canonicalId, onDuplicateClick }) {
  if (status === 'Duplicate' && isDuplicate) {
    return (
      <span
        className="bug-status-badge duplicate clickable"
        style={{ background: STATUS_COLORS[status] || '#bdbdbd' }}
        title={`Click to view canonical bug #${canonicalId}`}
        onClick={() => onDuplicateClick && onDuplicateClick(canonicalId)}
      >
        <FaTimes style={{ marginRight: 4 }} />
        Duplicate of #{canonicalId}
      </span>
    );
  }
  
  return (
    <span
      className="bug-status-badge"
      style={{ background: STATUS_COLORS[status] || '#bdbdbd' }}
    >
      {status === 'New' && <FaBug style={{ marginRight: 4 }} />}
      {status === 'Assigned' && <FaHourglassHalf style={{ marginRight: 4 }} />}
      {status === 'In Progress (Accepted)' && <FaHourglassHalf style={{ marginRight: 4 }} />}
      {status === 'Fixed' && <FaCheckCircle style={{ marginRight: 4 }} />}
      {status === 'Fixed (Verified)' && <FaCheckCircle style={{ marginRight: 4 }} />}
      {status.startsWith('Won\'t fix') && <FaTimes style={{ marginRight: 4 }} />}
      {status === 'Duplicate' && <FaTimes style={{ marginRight: 4 }} />}
      {status}
    </span>
  );
}

function PriorityBadge({ priority }) {
  return <span className={`bug-priority ${priority.toLowerCase()}`}>{priority}</span>;
}

function Toast({ message, onClose }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, 2500);
    return () => clearTimeout(t);
  }, [message, onClose]);
  if (!message) return null;
  return <div className="toast">{message}</div>;
}

// Comment component for displaying individual comments
function Comment({ comment, onEdit, onDelete, onReply, currentUser, isEditing, onSave, onCancel, level = 0, bugId }) {
  const [editContent, setEditContent] = useState(comment.content);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');

  const isAuthor = comment.authorEmail === currentUser?.email;
  const isAdmin = currentUser?.role === 'super';

  const handleSave = () => {
    onSave(comment._id, editContent);
  };

  const handleCancel = () => {
    setEditContent(comment.content);
    onCancel();
  };

  const handleReply = () => {
    if (replyContent.trim()) {
      onReply(bugId, comment._id, replyContent);
      setReplyContent('');
      setShowReplyForm(false);
    }
  };

  if (isEditing) {
    return (
      <div className="bug-comment editing">
        <div className="comment-header">
          <div className="comment-author">
            <FaUser /> {comment.author}
          </div>
          <div className="comment-time">
            <FaCalendar /> {new Date(comment.timestamp).toLocaleString()}
          </div>
        </div>
        <textarea
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          className="comment-edit-textarea"
          rows={3}
        />
        <div className="comment-actions">
          <button onClick={handleSave} className="btn-save">Save</button>
          <button onClick={handleCancel} className="btn-cancel">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bug-comment ${comment.isInternal ? 'internal' : ''}`} style={{ marginLeft: `${level * 20}px` }}>
      {/* Show parent comment if this is a reply */}
      {comment.parentCommentId && (
        <div className="parent-comment">
          <div className="parent-comment-header">
            <span className="parent-label">Replying to:</span>
            <span className="parent-author">{comment.parentCommentId.author}</span>
            <span className="parent-time">{new Date(comment.parentCommentId.timestamp).toLocaleString()}</span>
          </div>
          <div className="parent-comment-content">
            {comment.parentCommentId.content.split('\n').map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
        </div>
      )}
      
      <div className="comment-header">
        <div className="comment-author">
          <FaUser /> {comment.author}
          {comment.isInternal && <span className="internal-badge">IT Team</span>}
        </div>
        <div className="comment-time">
          <FaCalendar /> {new Date(comment.timestamp).toLocaleString()}
        </div>
        <div className="comment-actions">
          <button onClick={() => setShowReplyForm(!showReplyForm)} className="btn-reply">
            Reply
          </button>
          {(isAuthor || isAdmin) && (
            <>
              <button onClick={() => onEdit(comment._id)} className="btn-edit">
                <FaEdit />
              </button>
              <button onClick={() => onDelete(comment._id)} className="btn-delete">
                <FaTrash />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="comment-content">
        {comment.content.split('\n').map((line, index) => (
          <p key={index}>{line}</p>
        ))}
      </div>
      
      {/* Reply Form */}
      {showReplyForm && (
        <div className="reply-form">
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Write a reply..."
            rows={2}
            className="reply-textarea"
          />
          <div className="reply-actions">
            <button onClick={handleReply} disabled={!replyContent.trim()} className="btn-submit-reply">
              Reply
            </button>
            <button onClick={() => setShowReplyForm(false)} className="btn-cancel-reply">
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="comment-replies">
          {console.log('🔍 Rendering replies for comment', comment._id, ':', comment.replies)}
          {comment.replies.map(reply => (
            <Comment
              key={reply._id}
              comment={reply}
              currentUser={currentUser}
              isEditing={isEditing}
              onEdit={onEdit}
              onDelete={onDelete}
              onReply={onReply}
              onSave={onSave}
              onCancel={onCancel}
              level={level + 1}
              bugId={bugId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Bugs() {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [search, setSearch] = useState('');

  const [statusFilter, setStatusFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [toast, setToast] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [comments, setComments] = useState({}); // Store comments for each bug
  const [commentContent, setCommentContent] = useState({}); // Store comment content for each bug
  const [submittingComment, setSubmittingComment] = useState({}); // Track submitting state for each bug
  const [editingComment, setEditingComment] = useState(null);
  
  // Duplicate management state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedBugForDuplicate, setSelectedBugForDuplicate] = useState(null);
  const [canonicalSearchTerm, setCanonicalSearchTerm] = useState('');
  const [canonicalSearchResults, setCanonicalSearchResults] = useState([]);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // State for viewing canonical bug details
  const [showCanonicalModal, setShowCanonicalModal] = useState(false);
  const [canonicalBug, setCanonicalBug] = useState(null);
  const [canonicalLoading, setCanonicalLoading] = useState(false);
  const [duplicatesList, setDuplicatesList] = useState([]);

  const { token, user } = useContext(AuthContext);
  const searchRef = useRef();
  const debounceTimeout = useRef();

  // Load filters from localStorage on component mount
  useEffect(() => {
    const savedFilters = localStorage.getItem('bugsPageFilters');
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        setStatusFilter(filters.statusFilter || '');
        setModuleFilter(filters.moduleFilter || '');
        setPriorityFilter(filters.priorityFilter || '');
        setDateFilter(filters.dateFilter || '');
      } catch (error) {
        console.error('Error loading saved filters:', error);
      }
    }
  }, []);

  // Debug modal state changes
  useEffect(() => {
    if (showDuplicateModal) {
      console.log('🔍 Modal opened, selectedBugForDuplicate:', selectedBugForDuplicate);
    }
  }, [showDuplicateModal, selectedBugForDuplicate]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    const filters = {
      statusFilter,
      moduleFilter,
      priorityFilter,
      dateFilter
    };
    localStorage.setItem('bugsPageFilters', JSON.stringify(filters));
  }, [statusFilter, moduleFilter, priorityFilter, dateFilter]);

  // Add scrollable class to table wrapper for mobile scroll indicators
  useEffect(() => {
    const tableWrapper = document.querySelector('.bugs-table-wrapper');
    if (tableWrapper) {
      const checkScrollable = () => {
        // Use a small timeout to ensure DOM is fully rendered
        setTimeout(() => {
          if (tableWrapper.scrollWidth > tableWrapper.clientWidth) {
            tableWrapper.classList.add('scrollable');
          } else {
            tableWrapper.classList.remove('scrollable');
          }
        }, 100);
      };
      
      checkScrollable();
      window.addEventListener('resize', checkScrollable);
      
      // Also check when the component updates
      const observer = new MutationObserver(checkScrollable);
      observer.observe(tableWrapper, { 
        childList: true, 
        subtree: true, 
        attributes: true 
      });
      
      return () => {
        window.removeEventListener('resize', checkScrollable);
        observer.disconnect();
      };
    }
  }, [bugs]); // Re-run when bugs data changes

  useEffect(() => {
    fetch(`${API_BASE}/api/support/bugs`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          console.log('Bugs data received:', data);
          // Log attachment info for debugging
          data.forEach(bug => {
            if (bug.attachmentData || bug.attachment) {
              console.log(`Bug ${bug.ticketId} attachment:`, {
                attachmentData: bug.attachmentData,
                attachment: bug.attachment,
                absoluteUrl: getAbsoluteUrl(bug.attachmentData?.compressedUrl || bug.attachmentData?.originalUrl || bug.attachment)
              });
            }
          });
          setBugs(data);
        } else {
          setBugs([]);
        }
        setLoading(false);
      });
  }, [token]);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(debounceTimeout.current);
  }, [search]);

  // Optimized filtered bugs with useMemo
  const filteredBugs = useMemo(() => {
    return bugs.filter(bug => {
      // Status filter
      if (statusFilter && bug.status !== statusFilter) return false;
      
      // Module filter
      if (moduleFilter && bug.module !== moduleFilter) return false;
      
      // Priority filter
      if (priorityFilter && bug.priority !== priorityFilter) return false;
      
      // Date filter
      if (dateFilter) {
        const bugDate = new Date(bug.timestamp);
        const filterDate = new Date(dateFilter);
        const bugDateOnly = new Date(bugDate.getFullYear(), bugDate.getMonth(), bugDate.getDate());
        const filterDateOnly = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
        if (bugDateOnly.getTime() !== filterDateOnly.getTime()) return false;
      }
      
      // Search filter
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        return (
          bug.ticketId.toLowerCase().includes(searchLower) ||
          bug.issueTitle.toLowerCase().includes(searchLower) ||
          bug.name.toLowerCase().includes(searchLower) ||
          bug.description.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    });
  }, [bugs, statusFilter, moduleFilter, priorityFilter, dateFilter, debouncedSearch]);

  // Optimized progress bar logic with useMemo
  const progressStats = useMemo(() => {
    const total = bugs.length;
    const newBugs = bugs.filter(b => b.status === 'New').length;
    const inProgress = bugs.filter(b => ['Assigned', 'In Progress (Accepted)'].includes(b.status)).length;
    const completed = bugs.filter(b => ['Fixed', 'Fixed (Verified)', 'Won\'t fix (Infeasible)', 'Won\'t fix (Not reproducible)', 'Won\'t fix (Obsolete)', 'Won\'t fix (Intended behavior)', 'Duplicate'].includes(b.status)).length;
    
    return {
      total,
      newBugs,
      inProgress,
      completed,
      newPct: total ? (newBugs / total) * 100 : 0,
      inProgressPct: total ? (inProgress / total) * 100 : 0,
      completedPct: total ? (completed / total) * 100 : 0
    };
  }, [bugs]);

  const { total, newBugs, inProgress, completed, newPct, inProgressPct, completedPct } = progressStats;



  // Optimized unique modules for filter
  const moduleOptions = useMemo(() => {
    return Array.from(new Set(bugs.map(b => b.module).filter(Boolean))).sort();
  }, [bugs]);

  // Fetch comments for a specific bug
  const fetchComments = async (bugId) => {
    try {
      const response = await fetch(`${API_BASE}/api/support/bugs/${bugId}/comments`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (response.ok) {
        const commentsData = await response.json();
        console.log('🔍 Fetched comments for bug', bugId, ':', commentsData);
        setComments(prev => ({ ...prev, [bugId]: commentsData }));
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  // Expand/collapse row with comment fetching
  const toggleExpand = async (id) => {
    const newExpandedState = !expandedRows[id];
    setExpandedRows(r => ({ ...r, [id]: newExpandedState }));
    
    // Fetch comments when expanding
    if (newExpandedState && !comments[id]) {
      await fetchComments(id);
    }
  };

  // Submit a new comment
  const handleSubmitComment = async (bugId) => {
    const content = commentContent[bugId]?.trim();
    if (!content) return;

    setSubmittingComment(prev => ({ ...prev, [bugId]: true }));
    
    try {
      const response = await fetch(`${API_BASE}/api/support/bugs/${bugId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ content }),
      });

      if (response.ok) {
        const newComment = await response.json();
        setComments(prev => ({
          ...prev,
          [bugId]: [...(prev[bugId] || []), newComment]
        }));
        setCommentContent(prev => ({ ...prev, [bugId]: '' }));
        
        // Update bug's comment count in the main list
        setBugs(prev => prev.map(bug => 
          bug._id === bugId 
            ? { ...bug, commentCount: (bug.commentCount || 0) + 1 }
            : bug
        ));
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmittingComment(prev => ({ ...prev, [bugId]: false }));
    }
  };

  // Edit a comment
  const handleEditComment = (commentId) => {
    setEditingComment(commentId);
  };

  // Save edited comment
  const handleSaveComment = async (bugId, commentId, content) => {
    try {
      const response = await fetch(`${API_BASE}/api/support/bugs/${bugId}/comments/${commentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ content }),
      });

      if (response.ok) {
        const updatedComment = await response.json();
        setComments(prev => ({
          ...prev,
          [bugId]: prev[bugId].map(comment => 
            comment._id === commentId ? updatedComment : comment
          )
        }));
        setEditingComment(null);
      }
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  // Delete a comment
  const handleDeleteComment = async (bugId, commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/support/bugs/${bugId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });

      if (response.ok) {
        setComments(prev => ({
          ...prev,
          [bugId]: prev[bugId].filter(comment => comment._id !== commentId)
        }));
        
        // Update bug's comment count in the main list
        setBugs(prev => prev.map(bug => 
          bug._id === bugId 
            ? { ...bug, commentCount: Math.max(0, (bug.commentCount || 0) - 1) }
            : bug
        ));
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  // Cancel comment editing
  const handleCancelEdit = () => {
    setEditingComment(null);
  };

  // Handle reply to a comment
  const handleReplyToComment = async (bugId, parentCommentId, content) => {
    try {
      const response = await fetch(`${API_BASE}/api/support/bugs/${bugId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ content, parentCommentId }),
      });

      if (response.ok) {
        const newReply = await response.json();
        // Refresh comments to show the new reply
        await fetchComments(bugId);
        
        // Update bug's comment count in the main list
        setBugs(prev => prev.map(bug => 
          bug._id === bugId 
            ? { ...bug, commentCount: (bug.commentCount || 0) + 1 }
            : bug
        ));
      }
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  // Duplicate management functions
  const handleCancelDuplicate = () => {
    setShowDuplicateModal(false);
    setSelectedBugForDuplicate(null);
    setCanonicalSearchTerm('');
    setCanonicalSearchResults([]);
  };

  // Fetch canonical bug details and duplicates
  const fetchCanonicalDetails = async (canonicalId) => {
    try {
      setCanonicalLoading(true);
      
      // Fetch canonical bug details
      const canonicalResponse = await fetch(`${API_BASE}/api/support/bugs/${canonicalId}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });
      
      if (canonicalResponse.ok) {
        const canonicalData = await canonicalResponse.json();
        console.log('🔍 Canonical bug data received:', canonicalData);
        setCanonicalBug(canonicalData);
        
        // Fetch duplicates of this canonical bug
        const duplicatesResponse = await fetch(`${API_BASE}/api/support/bugs/${canonicalId}/duplicates`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
        });
        
        if (duplicatesResponse.ok) {
          const duplicatesData = await duplicatesResponse.json();
          console.log('🔍 Duplicates data received:', duplicatesData);
          setDuplicatesList(duplicatesData);
        }
      }
    } catch (error) {
      console.error('Error fetching canonical details:', error);
      setToast('Failed to fetch canonical bug details');
    } finally {
      setCanonicalLoading(false);
    }
  };

  // Handle clicking on duplicate reference or duplicate count
  const handleDuplicateClick = (id, isCanonical = false) => {
    console.log('🔍 [HANDLE_DUPLICATE_CLICK] Clicked on duplicate with ID:', id, 'isCanonical:', isCanonical);
    setShowCanonicalModal(true);
    if (isCanonical) {
      // If clicking on duplicate count of a canonical bug, fetch its details
      console.log('🔍 [HANDLE_DUPLICATE_CLICK] Fetching canonical bug details for ID:', id);
      fetchCanonicalDetails(id);
    } else {
      // If clicking on duplicate reference, fetch the canonical bug's details
      console.log('🔍 [HANDLE_DUPLICATE_CLICK] Fetching canonical bug details for duplicate ID:', id);
      fetchCanonicalDetails(id);
    }
  };

  // Handle closing canonical modal
  const handleCloseCanonicalModal = () => {
    setShowCanonicalModal(false);
    setCanonicalBug(null);
    setDuplicatesList([]);
  };

  const handleMarkAsDuplicate = async (duplicateId, canonicalId) => {
    console.log('🔍 Attempting to mark as duplicate:', { duplicateId, canonicalId });
    console.log('🔍 Current user role:', user?.role);
    console.log('🔍 Current user:', user);
    try {
      setDuplicateLoading(true);
      console.log('🔍 Sending request with token:', token ? 'Token present' : 'No token');
      const response = await fetch(`${API_BASE}/api/support/bugs/${duplicateId}/mark-duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ canonicalId }),
      });

      console.log('🔍 Response status:', response.status);
      if (response.ok) {
        const result = await response.json();
        console.log('🔍 Success response:', result);
        setToast(`Issue marked as duplicate of #${result.canonicalId}`);
        
        // Update the bug's status to "Duplicate" in the local state
        setBugs(bugs =>
          bugs.map(b => b._id === duplicateId ? { ...b, status: 'Duplicate', canonicalId: result.canonicalId } : b)
        );
        
        setShowDuplicateModal(false);
        setSelectedBugForDuplicate(null);
        setCanonicalSearchTerm('');
        setCanonicalSearchResults([]);
      } else {
        console.log('🔍 Error response status:', response.status);
        const error = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
        console.log('🔍 Error response:', error);
        setToast(error.error || 'Failed to mark as duplicate');
      }
    } catch (error) {
      setToast('Failed to mark as duplicate');
    } finally {
      setDuplicateLoading(false);
    }
  };

  const searchCanonicalIssues = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setCanonicalSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      console.log('🔍 Searching for canonical issues with term:', searchTerm);
      const response = await fetch(`${API_BASE}/api/support/bugs?search=${encodeURIComponent(searchTerm)}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Search response:', data);
        
        // Filter out the current bug and any bugs that are already duplicates
        const filteredResults = data.filter(bug => 
          bug._id !== selectedBugForDuplicate?._id && 
          bug.status !== 'Duplicate'
        );
        
        console.log('🔍 Filtered results:', filteredResults);
        setCanonicalSearchResults(filteredResults);
      } else {
        console.error('🔍 Search failed with status:', response.status);
        const errorText = await response.text();
        console.error('🔍 Search error response:', errorText);
      }
    } catch (error) {
      console.error('🔍 Error searching canonical issues:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleRemoveDuplicateStatus = async (bugId) => {
    if (!window.confirm('Are you sure you want to remove duplicate status from this issue?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/support/bugs/${bugId}/duplicate-status`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });

      if (response.ok) {
        setToast('Duplicate status removed');
        fetchBugs(); // Refresh the list
      } else {
        const error = await response.json();
        setToast(error.error || 'Failed to remove duplicate status');
      }
    } catch (error) {
      setToast('Failed to remove duplicate status');
    }
  };

  // Fetch bugs function for refresh
  const fetchBugs = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/support/bugs`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setBugs(data);
        } else {
          setBugs([]);
        }
      }
    } catch (error) {
      console.error('Error fetching bugs:', error);
      setToast('Failed to fetch bugs');
    }
  };

  // Status change
  const handleStatusChange = async (id, status) => {
    // Special handling for Duplicate status
    if (status === 'Duplicate') {
      const bug = bugs.find(b => b._id === id);
      console.log('🔍 Opening duplicate modal for bug:', bug);
      setSelectedBugForDuplicate(bug);
      setShowDuplicateModal(true);
      return; // Don't proceed with status update yet
    }

    setStatusUpdating(id);
    try {
      const response = await fetch(`${API_BASE}/api/support/bugs/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ status }),
      });
      
      if (response.ok) {
        setBugs(bugs =>
          bugs.map(b => (b._id === id ? { ...b, status } : b))
        );
        setToast(`Status updated to ${status}`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setToast(`Failed to update status: ${errorData.error || 'Server error'}`);
      }
    } catch (error) {
      console.error('Status change error:', error);
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        setToast('Cannot connect to server. Please check if the backend is running and try again.');
      } else {
        setToast(`Failed to update status: ${error.message || 'Network error'}`);
      }
    } finally {
      setStatusUpdating(null);
    }
  };

  // Archive a bug




  // Clear all filters
  const clearAllFilters = () => {
    setStatusFilter('');
    setModuleFilter('');
    setPriorityFilter('');
    setDateFilter('');
    setSearch('');
  };

  // Empty state
  const isEmpty = !loading && filteredBugs.length === 0;

  return (
    <div className="bugs-admin-page">
      <Toast message={toast} onClose={() => setToast('')} />
      <div className="bugs-header-row">
        <h1><FaBug style={{ color: '#e57373', marginRight: 8 }} /> Raised Tickets</h1>
        {/* Report Bug button removed */}
      </div>
      <div className="bugs-subtitle">Track system issues across modules. Stay updated on critical errors.</div>
      {/* Progress bar */}
      <div className="bugs-progress-bar clickable">
        <div className="bugs-progress-segment open" style={{ width: `${newPct}%` }} title={`New: ${newBugs} (${Math.round(newPct)}%)`} onClick={() => setStatusFilter('New')}>{newBugs > 0 && newBugs}</div>
        <div className="bugs-progress-segment inprogress" style={{ width: `${inProgressPct}%` }} title={`In Progress: ${inProgress} (${Math.round(inProgressPct)}%)`} onClick={() => setStatusFilter('')}>{inProgress > 0 && inProgress}</div>
        <div className="bugs-progress-segment resolved" style={{ width: `${completedPct}%` }} title={`Completed: ${completed} (${Math.round(completedPct)}%)`} onClick={() => setStatusFilter('')}>{completed > 0 && completed}</div>
      </div>
      <div className="bugs-progress-labels">
        <span><FaBug style={{ color: '#e57373' }} /> New: {newBugs}</span>
        <span><FaHourglassHalf style={{ color: '#ffb74d' }} /> In Progress: {inProgress}</span>
        <span><FaCheckCircle style={{ color: '#81c784' }} /> Completed: {completed}</span>
        <span style={{ marginLeft: 'auto', fontWeight: 500 }}>Total: {total}</span>
      </div>
      <div className="bugs-toolbar">
        <div className="bugs-search">
          <FaSearch />
          <input
            ref={searchRef}
            type="text"
            placeholder="Search by ticket, title, name, or description..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        
        <div className="bugs-filters">
          <select 
            className="bugs-filter-select" 
            value={statusFilter} 
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          
          <select 
            className="bugs-filter-select" 
            value={moduleFilter} 
            onChange={e => setModuleFilter(e.target.value)}
          >
            <option value="">All Modules</option>
            {moduleOptions.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          
          <select 
            className="bugs-filter-select" 
            value={priorityFilter} 
            onChange={e => setPriorityFilter(e.target.value)}
          >
            <option value="">All Priorities</option>
            {PRIORITY_OPTIONS.map(priority => (
              <option key={priority} value={priority}>{priority}</option>
            ))}
          </select>
          
          <div className="bugs-date-filter">
            <FaCalendarAlt style={{ marginRight: 6 }} />
            <input
              type="date"
              value={dateFilter}
              onChange={e => setDateFilter(e.target.value)}
              placeholder="Filter by date"
            />
          </div>
          
          <button 
            className="bugs-clear-filters-btn" 
            onClick={clearAllFilters}
            title="Clear all filters"
          >
            <FaTimes style={{ marginRight: 4 }} />
            Clear Filters
          </button>
        </div>
      </div>
      {loading ? (
        <div className="bugs-loading"><FaSpinner className="spin" /> Loading bugs 5</div>
      ) : isEmpty ? (
        <div className="bugs-empty">
          <span style={{ fontSize: '2.2em', color: '#e57373' }}>🐞</span>
          <div>No bug reports yet. Looks like everything’s working smoothly.</div>
          {/* Report Bug button removed */}
        </div>
      ) : (
        <div className="bugs-table-container">
          <div className="bugs-table-wrapper">
            <table className="bugs-table">
            <thead>
              <tr>
                <th></th>
                <th>Ticket</th>
                <th>Title</th>
                <th>Module</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Comments</th>
                <th>Duplicates</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBugs.map(bug => (
                <React.Fragment key={bug._id}>
                  <tr className={['Fixed', 'Fixed (Verified)', 'Won\'t fix (Infeasible)', 'Won\'t fix (Not reproducible)', 'Won\'t fix (Obsolete)', 'Won\'t fix (Intended behavior)', 'Duplicate'].includes(bug.status) ? 'resolved' : ''}>
                    <td>
                      <button className="bug-expand-btn" onClick={() => toggleExpand(bug._id)} title={expandedRows[bug._id] ? 'Collapse' : 'Expand'}>
                        {expandedRows[bug._id] ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                    </td>
                    <td>
                      <span className="bug-ticket" style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => window.location.href = `/admin/bugs/${bug._id}`}>
                        {bug.ticketId}
                      </span>
                    </td>
                    <td>
                      <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => window.location.href = `/admin/bugs/${bug._id}`}>
                        {bug.issueTitle}
                      </span>
                    </td>
                    <td>{MODULE_ICONS[bug.module] || MODULE_ICONS.default} {bug.module}</td>
                    <td><PriorityBadge priority={bug.priority} /></td>
                    <td><StatusBadge status={bug.status} bugId={bug._id} isDuplicate={bug.status === 'Duplicate'} canonicalId={bug.canonicalId} onDuplicateClick={(id) => handleDuplicateClick(id, false)} /></td>
                    <td>{new Date(bug.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                    <td>
                      <span className="comment-count" title={`${bug.commentCount || 0} comments`}>
                        💬 {bug.commentCount || 0}
                      </span>
                    </td>
                    <td>
                      {bug.isCanonical && bug.duplicateCount > 0 ? (
                        <span 
                          className="duplicate-count" 
                          title={`${bug.duplicateCount} duplicates - Click to view details`}
                          onClick={() => handleDuplicateClick(bug._id, true)}
                        >
                          <FaCopy /> {bug.duplicateCount}
                        </span>
                      ) : bug.status === 'Duplicate' ? (
                        <span className="duplicate-of" title={`Duplicate of #${bug.canonicalId}`}>
                          <FaCopy /> Duplicate
                        </span>
                      ) : (
                        <span className="no-duplicates">-</span>
                      )}
                    </td>
                    <td>
                      <div className="bug-actions">
                        <select
                          value={bug.status}
                          disabled={statusUpdating === bug._id}
                          onChange={e => handleStatusChange(bug._id, e.target.value)}
                          className="bug-status-select"
                          key={`${bug._id}-${bug.status}`} // Force re-render when status changes
                        >
                          <option value="New">New</option>
                          <option value="Assigned">Assigned</option>
                          <option value="In Progress (Accepted)">In Progress (Accepted)</option>
                          <option value="Fixed">Fixed</option>
                          <option value="Fixed (Verified)">Fixed (Verified)</option>
                          <option value="Won't fix (Infeasible)">Won't fix (Infeasible)</option>
                          <option value="Won't fix (Not reproducible)">Won't fix (Not reproducible)</option>
                          <option value="Won't fix (Obsolete)">Won't fix (Obsolete)</option>
                          <option value="Won't fix (Intended behavior)">Won't fix (Intended behavior)</option>
                          <option value="Duplicate">Duplicate</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                  {expandedRows[bug._id] && (
                    <tr className="bug-details-row">
                      <td colSpan={10}>
                                                 <div className="bug-details-inline">
                           <div><b>Reporter:</b> {bug.name} ({bug.email})</div>
                           <div><b>Description:</b> {bug.description}</div>
                           {bug.errorMessage && <div><b>Error Message:</b> {bug.errorMessage}</div>}
                           <div><b>Expected vs Actual:</b> {bug.expected}</div>
                           <div><b>Steps to Reproduce:</b> <ol>{(bug.steps || []).map((s, i) => <li key={i}>{s}</li>)}</ol></div>
                           {bug.status === 'Duplicate' && bug.canonicalId && (
                             <div className="duplicate-info">
                               <b>Duplicate of:</b> 
                               <span className="canonical-link" onClick={() => handleDuplicateClick(bug.canonicalId, false)}>
                                 #{bug.canonicalId}
                               </span>
                             </div>
                           )}
                          {bug.attachment && (
                            <div className="bug-attachment-section">
                              <b>Attachment:</b>
                              <div className="attachment-container">
                                <AttachmentPreview 
                                  attachment={getAbsoluteUrl(bug.attachment)} 
                                  key={`attachment-${bug._id}-${bug.attachment}`}
                                />
                              </div>
                            </div>
                          )}

                        </div>
                        
                        {/* Comments Section */}
                        <div className="bug-comments-section">
                          <h4>Comments ({comments[bug._id]?.length || 0})</h4>
                          
                          {/* Comment Form - only show for non-completed statuses */}
                          {!['Fixed', 'Fixed (Verified)', 'Won\'t fix (Infeasible)', 'Won\'t fix (Not reproducible)', 'Won\'t fix (Obsolete)', 'Won\'t fix (Intended behavior)', 'Duplicate'].includes(bug.status) && (
                            <div className="comment-form">
                            <textarea
                              value={commentContent[bug._id] || ''}
                              onChange={(e) => setCommentContent(prev => ({ ...prev, [bug._id]: e.target.value }))}
                              placeholder="Add a comment..."
                              rows={3}
                              className="comment-textarea"
                            />
                            <button 
                              onClick={() => handleSubmitComment(bug._id)}
                              disabled={submittingComment[bug._id] || !commentContent[bug._id]?.trim()}
                              className="btn-submit-comment"
                            >
                              {submittingComment[bug._id] ? <FaSpinner className="spin" /> : 'Add Comment'}
                            </button>
                          </div>
                          )}
                          
                          {/* Comments List */}
                          <div className="comments-list">
                                                         {comments[bug._id]?.map(comment => (
                               <Comment
                                 key={comment._id}
                                 comment={comment}
                                 currentUser={user}
                                 isEditing={editingComment === comment._id}
                                 onEdit={handleEditComment}
                                 onDelete={(commentId) => handleDeleteComment(bug._id, commentId)}
                                 onReply={(bugId, parentCommentId, content) => handleReplyToComment(bugId, parentCommentId, content)}
                                 onSave={(commentId, content) => handleSaveComment(bug._id, commentId, content)}
                                 onCancel={handleCancelEdit}
                                 bugId={bug._id}
                               />
                             ))}
                            {(!comments[bug._id] || comments[bug._id].length === 0) && (
                              <div className="no-comments">No comments yet. Be the first to add one!</div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
      
      {/* Duplicate Modal */}
      {showDuplicateModal && selectedBugForDuplicate && (
        <div className="modal-overlay" onClick={handleCancelDuplicate}>
          <div className="modal duplicate-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Mark as Duplicate</h3>
              <div className="user-role-debug">
                <small>User: {user?.email} | Role: {user?.role}</small>
              </div>
              <button onClick={handleCancelDuplicate}>×</button>
            </div>
            
            <div className="modal-body">
              <p>Marking <strong>#{selectedBugForDuplicate.ticketId}</strong> as duplicate of:</p>
              <p className="bug-title-preview"><em>"{selectedBugForDuplicate.issueTitle}"</em></p>
              
              <div className="canonical-search">
                <input
                  type="text"
                  placeholder="Search for canonical issue..."
                  value={canonicalSearchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCanonicalSearchTerm(value);
                    
                    // Clear previous timeout
                    if (window.searchTimeout) {
                      clearTimeout(window.searchTimeout);
                    }
                    
                    // Debounce the search
                    window.searchTimeout = setTimeout(() => {
                      searchCanonicalIssues(value);
                    }, 300);
                  }}
                />
                {searchLoading && <FaSpinner className="spin" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />}
              </div>

              <div className="canonical-results">
                {!canonicalSearchTerm && (
                  <div className="search-instructions">
                    Start typing to search for the canonical issue this is a duplicate of...
                  </div>
                )}
                
                {canonicalSearchResults.map(bug => (
                  <div key={bug._id} className="canonical-option">
                    <div className="canonical-info">
                      <strong>#{bug.ticketId}</strong> - {bug.issueTitle}
                      <span className="canonical-status">{bug.status}</span>
                    </div>
                    <button 
                      onClick={() => handleMarkAsDuplicate(selectedBugForDuplicate._id, bug._id)}
                      className="btn-mark-duplicate"
                      disabled={duplicateLoading}
                    >
                      {duplicateLoading ? <FaSpinner className="spin" /> : 'Mark as Duplicate'}
                    </button>
                  </div>
                ))}
                
                {canonicalSearchTerm && searchLoading && (
                  <div className="search-loading">Searching...</div>
                )}
                
                {canonicalSearchTerm && !searchLoading && canonicalSearchResults.length === 0 && (
                  <div className="no-results">No issues found matching your search.</div>
                )}
              </div>
              
              <div className="modal-actions">
                <button onClick={handleCancelDuplicate} className="btn-cancel">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Canonical Bug Modal */}
      {showCanonicalModal && canonicalBug && (
        <div className="modal-overlay" onClick={handleCloseCanonicalModal}>
          <div className="modal canonical-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Canonical Bug #{canonicalBug.ticketId}</h3>
              <button onClick={handleCloseCanonicalModal}>×</button>
            </div>
            
            <div className="modal-body">
              {canonicalLoading ? (
                <div className="loading-spinner">
                  <FaSpinner className="spin" /> Loading...
                </div>
              ) : (
                <>
                  <div className="canonical-details">
                    <h4>{canonicalBug.issueTitle}</h4>
                    <div className="bug-meta">
                      <span className="bug-status">
                        <StatusBadge status={canonicalBug.status} />
                      </span>
                      <span className="bug-priority">
                        <PriorityBadge priority={canonicalBug.priority} />
                      </span>
                      <span className="bug-module">{canonicalBug.module}</span>
                    </div>
                    <p className="bug-description">{canonicalBug.description}</p>
                    {canonicalBug.errorMessage && (
                      <div className="bug-error">
                        <strong>Error Message:</strong> {canonicalBug.errorMessage}
                      </div>
                    )}
                    {canonicalBug.stepsToReproduce && (
                      <div className="bug-steps">
                        <strong>Steps to Reproduce:</strong> {canonicalBug.stepsToReproduce}
                      </div>
                    )}
                    <div className="bug-reporter">
                      <strong>Reported by:</strong> {canonicalBug.name || canonicalBug.reporterName || 'Unknown'} ({canonicalBug.email || canonicalBug.reporterEmail || 'Unknown'})
                    </div>
                    <div className="bug-timestamp">
                      <strong>Reported on:</strong> {new Date(canonicalBug.timestamp).toLocaleString()}
                    </div>
                    {canonicalBug.attachment && (
                      <div className="canonical-attachment-section">
                        <strong>Attachment:</strong>
                        <div className="attachment-container">
                          <AttachmentPreview attachment={getAbsoluteUrl(canonicalBug.attachment)} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="duplicates-section">
                    <h4>Duplicates ({duplicatesList.length})</h4>
                    {duplicatesList.length > 0 ? (
                      <div className="duplicates-list">
                        {console.log('🔍 Rendering duplicates:', duplicatesList)}
                        {duplicatesList.map(duplicate => {
                          console.log('🔍 Individual duplicate:', duplicate);
                          console.log('🔍 Duplicate issue data:', duplicate.duplicateIssueId);
                          console.log('🔍 Available fields:', Object.keys(duplicate.duplicateIssueId || {}));
                          return (
                          <div key={duplicate._id} className="duplicate-item clickable" onClick={() => handleDuplicateClick(duplicate.duplicateIssueId._id, false)}>
                            <div className="duplicate-info">
                              <strong>#{duplicate.duplicateIssueId?.ticketId || 'N/A'}</strong> - {duplicate.duplicateIssueId?.issueTitle || 'No title'}
                              <span className="duplicate-status">{duplicate.duplicateIssueId?.status || 'Unknown'}</span>
                            </div>
                            <div className="duplicate-meta">
                              <span className="duplicate-reporter">{duplicate.duplicateIssueId?.reporterName || duplicate.duplicateIssueId?.name || 'Unknown'}</span>
                              <span className="duplicate-date">
                                {duplicate.duplicateIssueId?.timestamp ? new Date(duplicate.duplicateIssueId.timestamp).toLocaleDateString() : 
                                 duplicate.duplicateIssueId?.createdAt ? new Date(duplicate.duplicateIssueId.createdAt).toLocaleDateString() : 
                                 'Invalid Date'}
                              </span>
                            </div>
                            <div className="duplicate-description">
                              {duplicate.duplicateIssueId?.description || 'No description available'}
                            </div>
                            {duplicate.duplicateIssueId?.errorMessage && (
                              <div className="duplicate-error-message">
                                <strong>Error:</strong> {duplicate.duplicateIssueId.errorMessage}
                              </div>
                            )}
                            {duplicate.duplicateIssueId?.expected && (
                              <div className="duplicate-expected">
                                <strong>Expected:</strong> {duplicate.duplicateIssueId.expected}
                              </div>
                            )}
                            <div className="duplicate-module">
                              <strong>Module:</strong> {duplicate.duplicateIssueId?.module || 'Unknown'}
                            </div>
                            <div className="duplicate-priority">
                              <strong>Priority:</strong> {duplicate.duplicateIssueId?.priority || 'Unknown'}
                            </div>
                            {duplicate.duplicateIssueId?.attachment && (
                              <div className="duplicate-attachment-section">
                                <strong>Attachment:</strong>
                                <div className="attachment-container">
                                  <AttachmentPreview attachment={getAbsoluteUrl(duplicate.duplicateIssueId.attachment)} />
                                </div>
                              </div>
                            )}
                            <div className="duplicate-click-hint">
                              <small>Click to view full details</small>
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    ) : (
                      <p className="no-duplicates">No duplicates found.</p>
                    )}
                    {duplicatesList.length > 0 && (
                      <div className="duplicates-actions">
                        <button 
                          className="btn-view-all-duplicates"
                          onClick={() => {
                            // You could implement a more detailed view here
                            setToast(`Showing ${duplicatesList.length} duplicates of this canonical bug`);
                          }}
                        >
                          View All Duplicates
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 